/**
 * Network connections calculation for Team Shape visualization
 * Calculates connections between player clusters based on passes, dribbles, etc.
 */

import { VisualizationEvent } from '../components/pitch/types';
import { Cluster } from './clustering';
import { getPositionExtractionConfig } from './teamShapePositionExtraction';

export interface NetworkConnection {
  fromClusterIndex: number;
  toClusterIndex: number;
  strength: number; // Weighted connection strength
  eventCount: number; // Number of events contributing to this connection
}

/**
 * Calculate Euclidean distance between two points
 */
function euclideanDistance(
  p1: { x: number; y: number },
  p2: { x: number; y: number }
): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Find the closest cluster to a given point
 */
function findClosestCluster(
  point: { x: number; y: number },
  clusters: Cluster[]
): number | null {
  if (clusters.length === 0) return null;

  let minDistance = Infinity;
  let closestIndex = 0;

  clusters.forEach((cluster, index) => {
    const distance = euclideanDistance(point, cluster.centroid);
    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = index;
    }
  });

  return closestIndex;
}

/**
 * Calculate network connections between clusters based on events
 * Only considers events that have both source and destination (passes, dribbles, crosses, corners)
 */
export function calculateNetworkConnections(
  events: VisualizationEvent[],
  clusters: Cluster[],
  statKeyMap: Map<string, string> // stat_type_id -> stat_key
): NetworkConnection[] {
  if (clusters.length === 0) return [];

  // Map to store connections: "fromIndex-toIndex" -> { strength, eventCount }
  const connectionMap = new Map<string, { strength: number; eventCount: number }>();

  events.forEach((event) => {
    // Skip events without position data
    if (!event.position_data || event.position_data.length === 0) return;

    // Get stat key for this event
    const statKey = statKeyMap.get(event.stat_type_id);
    if (!statKey) return;

    // Get position extraction config
    const config = getPositionExtractionConfig(statKey);

    // Only consider events that use both source and destination for network connections
    // This includes: passes, dribbles, crosses, corners
    if (!config.useSource || !config.useDestination || config.skip) return;

    // Extract source and destination points
    const source = event.position_data[0];
    const destination = event.position_data[event.position_data.length - 1];

    // Skip if source and destination are the same (no meaningful connection)
    if (source.x === destination.x && source.y === destination.y) return;

    // Find closest clusters for source and destination
    const sourceClusterIndex = findClosestCluster(source, clusters);
    const destClusterIndex = findClosestCluster(destination, clusters);

    // Skip if either point doesn't belong to a cluster or if they're the same cluster
    if (
      sourceClusterIndex === null ||
      destClusterIndex === null ||
      sourceClusterIndex === destClusterIndex
    ) {
      return;
    }

    // Calculate connection strength (weighted by event weight)
    const eventWeight = config.eventWeight ?? 1.0;
    const connectionStrength = eventWeight;

    // Preserve actual direction: FROM source cluster TO destination cluster
    // This represents the actual flow: passes FROM passer TO receiver, dribbles FROM start TO end
    const fromIndex = sourceClusterIndex;
    const toIndex = destClusterIndex;
    const connectionKey = `${fromIndex}-${toIndex}`;

    // Update connection map
    const existing = connectionMap.get(connectionKey);
    if (existing) {
      existing.strength += connectionStrength;
      existing.eventCount += 1;
    } else {
      connectionMap.set(connectionKey, {
        strength: connectionStrength,
        eventCount: 1,
      });
    }
  });

  // Convert map to array of NetworkConnection objects
  const connections: NetworkConnection[] = [];
  connectionMap.forEach((value, key) => {
    const [fromIndexStr, toIndexStr] = key.split('-');
    connections.push({
      fromClusterIndex: parseInt(fromIndexStr, 10),
      toClusterIndex: parseInt(toIndexStr, 10),
      strength: value.strength,
      eventCount: value.eventCount,
    });
  });

  return connections;
}

